import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, CircleDot, Lock, Pencil, Trash2, Truck } from "lucide-react";
import { PageActions, PageBody } from "@/components/ui/PageHeader";
import { SupplierRegionBadge } from "@/components/suppliers/SupplierRegionBadge";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { Button, buttonStyles } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { Badge } from "@/components/ui/Badge";
import { SupplierDocuments } from "@/components/suppliers/SupplierDocuments";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useDeleteSupplier, useSupplier, useSupplierPortal } from "@/api/hooks";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { formatDate } from "@/domain/datetime";
import { cn } from "@/lib/cn";

function dash(value: React.ReactNode): React.ReactNode {
  if (value == null || value === "") return "—";
  return value;
}

function Meta({
  label,
  children,
  mono,
  className,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      <dt className="text-2xs uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className={cn("break-words text-sm text-ink", mono && "font-mono tabular")}>{dash(children)}</dd>
    </div>
  );
}

function ContactLink({
  href,
  children,
  mono,
}: {
  href: string;
  children: string;
  mono?: boolean;
}) {
  return (
    <a
      href={href}
      className={cn("text-brand hover:underline", mono && "font-mono tabular")}
    >
      {children}
    </a>
  );
}

export function SupplierDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const listTo = `/suppliers${search}`;
  const saved = typeof state === "object" && state !== null && "supplierSaved" in state && state.supplierSaved === true;
  const { user } = useAuth();
  const can = useCan();
  const { data: supplier, isLoading, error, refetch } = useSupplier(id);
  const del = useDeleteSupplier();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actErr, setActErr] = useState<string | null>(null);

  const canSeeCost = user ? can("supplier_cost:view") : false;
  const canUpdate = user ? can("records:update") : false;
  const canDelete = user ? can("records:delete") : false;
  // Trạng thái cổng hiện ngay đầu trang cho người quản lý cổng (trước chỉ thấy khi bấm Sửa); API cổng cũng chỉ mở cho họ.
  const canManagePortal = user ? can("users:manage") : false;
  const portal = useSupplierPortal(id, canManagePortal);

  usePageCrumbs(
    supplier ? [{ label: supplier.companyName }] : error ? [{ label: t("suppliers.notFound") }] : [],
    listTo,
  );

  if (isLoading) {
    return (
      <PageBody>
        <LoadingState label={t("suppliers.loading")} />
      </PageBody>
    );
  }
  if (error || !supplier) {
    const missing =
      !error || (error instanceof ApiClientError && (error.status === 404 || error.code === "not_found"));
    return (
      <PageBody>
        <ErrorState
          message={missing ? t("suppliers.notFound") : t("suppliers.errorLoad")}
          onRetry={error && !missing ? () => refetch() : undefined}
        />
      </PageBody>
    );
  }

  const doDelete = async () => {
    setActErr(null);
    try {
      await del.mutateAsync(supplier.id);
      navigate(listTo);
    } catch (err) {
      setActErr(getLocalizedError(err, t, "suppliers.errorDelete"));
    }
  };

  const contacts = [...supplier.contacts].sort((a, b) => a.sortOrder - b.sortOrder);
  // Điện thoại 2 và WeChat: gần như không ai dùng (production 19/09/2026: 1/9 và 0/9) — chỉ hiện cột khi có người liên hệ dùng.
  const showPhone2 = contacts.some((c) => !!c.phone2?.trim());
  const showWechat = contacts.some((c) => !!c.wechat?.trim());

  return (
    <PageBody className="space-y-4">
      <ConfirmDialog
        open={confirmDelete}
        title={supplier.companyName}
        description={t("suppliers.confirmDelete")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => void doDelete()}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={listTo} className={buttonStyles({ variant: "ghost", size: "sm" })}>
          <ArrowLeft className="size-4" /> {t("suppliers.backToList")}
        </Link>
        <PageActions>
          {canSeeCost && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/products?supplier=${supplier.id}`)}
            >
              <CircleDot className="size-4" /> {t("suppliers.viewProducts")}
            </Button>
          )}
          {canUpdate && (
            <Link to={`/suppliers/${supplier.id}/edit${search}`}
              className={buttonStyles({ variant: "secondary", size: "sm" })}>
              <Pencil className="size-4" /> {t("common.edit")}
            </Link>
          )}
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              aria-label={t("common.delete")}
              title={t("common.delete")}
              disabled={del.isPending}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </PageActions>
      </div>
      {saved && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{t("common.saved")}</p>}

      {actErr && (
        <p className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{actErr}</p>
      )}

      <Panel className="overflow-hidden">
        <div className="flex flex-col sm:flex-row">
          <div className="grid h-32 w-full shrink-0 place-items-center bg-[color:var(--color-brass-tint)] text-[color:var(--color-brass)] sm:h-auto sm:min-h-32 sm:w-32">
            <Truck className="size-10" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 p-5">
            <h1 className="break-words text-2xl font-semibold tracking-tight text-ink">{supplier.companyName}</h1>
            <p className="break-words text-sm text-ink-soft">{dash(supplier.address)}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span title={t("suppliers.classification")}><SupplierRegionBadge classification={supplier.classification} /></span>
              {supplier.isPrivate ? (
                <Badge tone="neutral" title={t("suppliers.privateHint")}><Lock className="size-3" aria-hidden="true" /> {t("suppliers.privateBadge")}</Badge>
              ) : null}
              <p className="text-xs text-ink-faint">
                {t("suppliers.contactsCount", { count: supplier.contactsCount })}
              </p>
              {portal.data && (
                <Link to={`/suppliers/${supplier.id}/edit${search}`} className="inline-flex items-center gap-1.5 text-xs text-ink-soft hover:underline" title={t("suppliers.portalSection")}>
                  <span>{t("suppliers.portalSection")}:</span>
                  {portal.data.account ? (
                    <Badge tone={portal.data.account.status === "active" ? "ok" : "danger"}>
                      {t(portal.data.account.status === "active" ? "suppliers.portalStatusActive" : "suppliers.portalStatusDisabled")}
                    </Badge>
                  ) : (
                    <Badge tone="neutral" outline>{t("suppliers.portalNoAccount")}</Badge>
                  )}
                  {portal.data.loginId && <span className="font-mono tabular">{portal.data.loginId}</span>}
                </Link>
              )}
            </div>
          </div>
        </div>
      </Panel>

      <Panel>
        <PanelHeader title={t("suppliers.sectionCompany")} />
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-3">
          <Meta label={t("suppliers.phone")} mono>
            {supplier.phone ? (
              <ContactLink href={`tel:${supplier.phone}`} mono>
                {supplier.phone}
              </ContactLink>
            ) : null}
          </Meta>
          <Meta label={t("suppliers.email")}>
            {supplier.email ? (
              <ContactLink href={`mailto:${supplier.email}`}>{supplier.email}</ContactLink>
            ) : null}
          </Meta>
          <Meta label={t("products.created")} mono>
            {`${formatDate(supplier.createdAt)}${supplier.createdByName ? ` · ${supplier.createdByName}` : ""}`}
          </Meta>
          {/* Người theo dõi (0097) — với nhà cung cấp riêng tư cũng là những người được thấy, ngoài quản trị viên. */}
          <Meta label={t("suppliers.followers")}>
            {supplier.followers.length ? supplier.followers.map((f) => f.name).join(", ") : null}
          </Meta>
        </dl>
      </Panel>

      <SupplierDocuments supplierId={supplier.id} />

      <Panel>
        <PanelHeader title={t("suppliers.contacts")} />
        {contacts.length === 0 ? (
          <p className="px-4 py-6 text-sm text-ink-faint">{t("suppliers.noContacts")}</p>
        ) : (
          <TableShell className="rounded-none border-0">
            <thead>
              <tr>
                <Th className="w-8" align="right">#</Th>
                <Th>{t("suppliers.contactName")}</Th>
                <Th>{t("suppliers.specialization")}</Th>
                <Th>{t("suppliers.phone1")}</Th>
                {showPhone2 && <Th>{t("suppliers.phone2")}</Th>}
                <Th>{t("suppliers.email")}</Th>
                {showWechat && <Th>{t("suppliers.wechat")}</Th>}
              </tr>
            </thead>
            <tbody>
              {contacts.map((c, i) => (
                <Tr key={c.id}>
                  <Td align="right" className="font-mono text-xs text-ink-faint">
                    {String(i + 1).padStart(2, "0")}
                  </Td>
                  <Td className="font-medium text-ink">{c.name}</Td>
                  <Td className="text-xs text-ink-soft">{dash(c.specialization)}</Td>
                  <Td className="font-mono tabular text-xs">
                    {c.phone1 ? (
                      <ContactLink href={`tel:${c.phone1}`} mono>
                        {c.phone1}
                      </ContactLink>
                    ) : (
                      "—"
                    )}
                  </Td>
                  {showPhone2 && (
                    <Td className="font-mono tabular text-xs">
                      {c.phone2 ? (
                        <ContactLink href={`tel:${c.phone2}`} mono>
                          {c.phone2}
                        </ContactLink>
                      ) : (
                        "—"
                      )}
                    </Td>
                  )}
                  <Td className="text-xs">
                    {c.email ? <ContactLink href={`mailto:${c.email}`}>{c.email}</ContactLink> : "—"}
                  </Td>
                  {showWechat && <Td className="font-mono tabular text-xs text-ink-soft">{dash(c.wechat)}</Td>}
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </Panel>
    </PageBody>
  );
}
